import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { LinearProgress, Stack, Typography, linearProgressClasses, styled } from "@mui/material";

import { getToken } from "auth/helpers/authApi";

import { ActivityStatus, UserJourneyRouteResponse } from "user-journey/consts/journeyActivity";

import { apiClient } from "utils/apiClient";
import { getInvertColor } from "utils/themeUtils";

const JourneyProgressBar = styled(LinearProgress)(({ theme }) => ({
	height: "0.8rem",
	borderRadius: "1.6rem",
	backgroundColor:
		getInvertColor(theme.palette.primary.main) === "#3C4255" ? "#3C4255" : theme.palette.primary["100"],

	[`& .${linearProgressClasses.bar}`]: {
		borderRadius: "1.6rem",
		backgroundColor: theme.palette.success.main,
	},
}));

export default function UserJourneyProgress() {
	const [journeyRoutes, setJourneyRoutes] = useState<UserJourneyRouteResponse[]>([]);

	const fetchUserJourney = useCallback(async () => {
		try {
			const res = await apiClient<UserJourneyRouteResponse[]>(`user-journey/routes`, {
				token: getToken(),
			});

			if (res) {
				setJourneyRoutes(res.data);
			}
		} catch (error) {}
	}, []);

	useEffect(() => {
		fetchUserJourney();
	}, [fetchUserJourney]);

	const totalActivities = journeyRoutes
		.map((journeyRoute) => journeyRoute.activities.length)
		.reduce((a, b) => a + b, 0);

	const completedActivities = journeyRoutes.flatMap((journeyRoute) =>
		journeyRoute.activities.filter((activity) => activity.status === ActivityStatus.COMPLETED),
	).length;
	const progress = (completedActivities / totalActivities) * 100;

	return (
		<Stack gap="1.2rem">
			<Stack>
				<Typography
					color={(theme) => getInvertColor(theme.palette.primary.main)}
					fontSize="1.7rem"
					className="user-journey-progress-info"
				>
					<FormattedMessage
						id="USER-JOURNEY.header.activity-progress"
						values={{
							completedActivities,
							totalActivities,
						}}
					/>
				</Typography>
			</Stack>
			<Stack>
				<JourneyProgressBar value={progress} variant="determinate" />
			</Stack>
		</Stack>
	);
}
